import React, { useState, MouseEvent } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  CircularProgress,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Warehouse as WarehouseIcon,
  Mic as VoiceAssistantIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  Logout as LogoutIcon,
  Help as HelpIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  MarkEmailUnread as MarkEmailUnreadIcon,
  DeleteOutline as DeleteOutlineIcon,
  Menu as MenuIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import { useWarehouse } from "../../contexts/WarehouseContext";
import { useNavigate } from "react-router-dom";
import {
  getUserNotifications,
  setUserNotificationReadState,
  deleteUserNotification,
  UserNotification,
  userNotificationsEvents,
} from "../../utils/userNotifications";

interface NavBarProps {
  drawerWidth: number;
  handleDrawerToggle: () => void;
  onOpenVoicePanel?: () => void;
}

const unifiedButtonStyle = {
  backgroundColor: "#46464644",
  border: "1px solid #474747ff",
  borderRadius: "11px",
  height: 40,
  minWidth: 40,
  padding: "0px 12px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  cursor: "pointer",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#46464688",
    transform: "scale(1.05)",
  },
};

const avatarButtonStyle = {
  ...unifiedButtonStyle,
  padding: "0 12px",
  gap: 1,
  "&:hover": {
    backgroundColor: "#46464688",
    transform: "scale(1.05)",
  },
};

const languageButtonStyle = {
  ...unifiedButtonStyle,
  minWidth: 48,
  "&:hover": {
    backgroundColor: "#46464688",
    transform: "scale(1.05)",
  },
};

const NavBar: React.FC<NavBarProps> = ({ drawerWidth, handleDrawerToggle, onOpenVoicePanel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { warehouses, selectedWarehouse, setSelectedWarehouse, loading: warehousesLoading } = useWarehouse();
  const navigate = useNavigate();

  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [notificationsAnchor, setNotificationsAnchor] =
    useState<null | HTMLElement>(null);
  const [warehouseAnchor, setWarehouseAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [chatVoiceAnchor, setChatVoiceAnchor] = useState<null | HTMLElement>(null);

  const [language, setLanguage] = useState(
    localStorage.getItem("language") || "pl",
  );

  const [notifications, setNotifications] = useState<UserNotification[]>(() => getUserNotifications());

  React.useEffect(() => {
    const loadNotifications = () => {
      setNotifications(getUserNotifications());
    };

    loadNotifications();
    window.addEventListener(userNotificationsEvents.UPDATE_EVENT, loadNotifications);

    return () => {
      window.removeEventListener(userNotificationsEvents.UPDATE_EVENT, loadNotifications);
    };
  }, []);

  const formatNotificationTime = (isoDate: string) => {
    const createdAt = new Date(isoDate).getTime();
    const now = Date.now();
    const diffMs = Math.max(now - createdAt, 0);
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 1) {
      return t("notifications.justNow");
    }

    if (diffMinutes < 60) {
      return t("notifications.minutesAgo", { count: diffMinutes });
    }

    if (diffHours < 24) {
      return t("notifications.hoursAgo", { count: diffHours });
    }

    return t("notifications.daysAgo", { count: diffDays });
  };

  const getNotificationText = (notification: UserNotification) => {
    if (notification.translationKey) {
      const documentTypeCode = notification.translationParams?.documentTypeCode;
      const documentType = documentTypeCode
        ? (t(`documents.types.${documentTypeCode}`) || documentTypeCode)
        : undefined;

      return t(notification.translationKey, {
        ...notification.translationParams,
        documentType,
        defaultValue: notification.text,
      });
    }

    return notification.text;
  };

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleLanguageChange = () => {
    const newLang = language === "en" ? "pl" : "en";
    setLanguage(newLang);
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const handleOpenMenu =
    (setState: React.Dispatch<React.SetStateAction<HTMLElement | null>>) =>
      (event: MouseEvent<HTMLElement>) => {
        setState(event.currentTarget);
      };

  const handleCloseMenu =
    (setState: React.Dispatch<React.SetStateAction<HTMLElement | null>>) =>
      () =>
        setState(null);

  const handleLogout = () => {
    logout();
    navigate("/login");
    handleCloseMenu(setSettingsAnchor)();
  };

  const getUserInitials = () => {
    if (user?.name) {
      const names = user.name.split(" ");
      return names
        .map((n: string) => n[0])
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return user?.username?.slice(0, 2).toUpperCase() || "U";
  };

  const getDisplayName = () => {
    return user?.name || user?.username || t("navbar.unknownUser");
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        width: { sm: `calc(100% - ${drawerWidth}px)` },
        ml: { sm: `${drawerWidth}px` },
        bgcolor: "background.default",
        color: "text.primary",
        boxShadow: "none",
        borderBottomLeftRadius: "20px",
        borderBottomRightRadius: "20px",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", gap: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {isMobile && (
            <IconButton
              color="inherit"
              aria-label={t("navbar.openDrawer")}
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Tooltip title={t("navbar.selectWarehouseTooltip")}>
            <span style={{ display: "inline-block" }}>
              <IconButton
                color="inherit"
                sx={unifiedButtonStyle}
                onClick={handleOpenMenu(setWarehouseAnchor)}
                disabled={warehousesLoading}
              >
                {warehousesLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <WarehouseIcon />
                )}
                <Typography
                  variant="body2"
                  sx={{ ml: 1, display: { xs: "none", sm: "block" } }}
                >
                  {selectedWarehouse?.name || t("navbar.selectWarehouse")}
                </Typography>
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={warehouseAnchor}
            open={Boolean(warehouseAnchor)}
            onClose={handleCloseMenu(setWarehouseAnchor)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "center",
            }}
            PaperProps={{
              sx: {
                bgcolor: "primary.main",
                "& .MuiMenuItem-root": {
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                },
              },
            }}
          >
            {warehouses.length === 0 ? (
              <MenuItem disabled>
                {t("navbar.noWarehouses")}
              </MenuItem>
            ) : (
              warehouses.map((warehouse) => (
                <MenuItem
                  key={warehouse.id}
                  selected={selectedWarehouse?.id === warehouse.id}
                  onClick={() => {
                    setSelectedWarehouse(warehouse);
                    handleCloseMenu(setWarehouseAnchor)();
                  }}
                >
                  {warehouse.name} - {warehouse.city}
                </MenuItem>
              ))
            )}
          </Menu>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isMobile ? (
            <>
              <Tooltip title={t("voicePanel.andChatTooltip")}>
                <IconButton
                  color="inherit"
                  sx={unifiedButtonStyle}
                  onClick={handleOpenMenu(setChatVoiceAnchor)}
                  aria-haspopup="true"
                  aria-expanded={Boolean(chatVoiceAnchor)}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={chatVoiceAnchor}
                open={Boolean(chatVoiceAnchor)}
                onClose={handleCloseMenu(setChatVoiceAnchor)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                PaperProps={{
                  sx: { minWidth: 220, bgcolor: "#2a2a2a", backgroundImage: "none" },
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleCloseMenu(setChatVoiceAnchor)();
                    onOpenVoicePanel ? onOpenVoicePanel() : navigate("/chat");
                  }}
                >
                  <VoiceAssistantIcon sx={{ mr: 1.5 }} fontSize="small" />
                  {t("voicePanel.title")}
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    handleCloseMenu(setChatVoiceAnchor)();
                    navigate("/chat");
                  }}
                >
                  <ChatIcon sx={{ mr: 1.5 }} fontSize="small" />
                  {t("chat.menuItem")}
                </MenuItem>
              </Menu>
            </>
          ) : (
            <>
              <Tooltip title={t("voicePanel.voiceAssistantTooltip")}>
                <IconButton
                  color="inherit"
                  sx={unifiedButtonStyle}
                  onClick={() => (onOpenVoicePanel ? onOpenVoicePanel() : navigate("/chat"))}
                >
                  <VoiceAssistantIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("chat.menuItem")}>
                <IconButton
                  color="inherit"
                  sx={unifiedButtonStyle}
                  onClick={() => navigate("/chat")}
                >
                  <ChatIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          <Tooltip title={t("notifications.title")}>
            <IconButton
              color="inherit"
              onClick={handleOpenMenu(setNotificationsAnchor)}
              sx={unifiedButtonStyle}
            >
              <Badge badgeContent={unreadCount} color="primary">
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>

          <Tooltip title={t("navbar.changeLanguage")}>
            <Box onClick={handleLanguageChange} sx={languageButtonStyle}>
              <Typography
                sx={{
                  minWidth: 24,
                  textAlign: "center",
                  fontSize: "0.875rem",
                  fontWeight: "bold",
                }}
              >
                {language.toUpperCase()}
              </Typography>
            </Box>
          </Tooltip>

          <Menu
            anchorEl={notificationsAnchor}
            open={Boolean(notificationsAnchor)}
            onClose={handleCloseMenu(setNotificationsAnchor)}
            PaperProps={{
              sx: { width: 460, maxWidth: "95vw", maxHeight: 460, bgcolor: "#2a2a2a", backgroundImage: "none" },
            }}
          >
            <MenuItem disabled>
              <Typography fontWeight="bold">
                {t("notifications.title")} ({t("notifications.newCount", { count: unreadCount })})
              </Typography>
            </MenuItem>

            {notifications.map((notification) => (
              <MenuItem
                key={notification.id}
                sx={{
                  bgcolor: notification.read ? "transparent" : "action.hover",
                  borderLeft: notification.read ? "none" : "3px solid",
                  borderColor: "primary.main",
                  alignItems: "flex-start",
                  gap: 1,
                }}
              >
                <Box sx={{ flexGrow: 1, minWidth: 0, pr: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      whiteSpace: "normal",
                      overflowWrap: "anywhere",
                      wordBreak: "break-word",
                      lineHeight: 1.35,
                    }}
                  >
                    {getNotificationText(notification)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatNotificationTime(notification.createdAt)}
                  </Typography>
                </Box>
                <Tooltip title={notification.read ? t("notifications.markAsUnread") : t("notifications.markAsRead")}>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      setUserNotificationReadState(notification.id, !notification.read);
                    }}
                    sx={{ mt: 0.25, color: "common.white" }}
                  >
                    {notification.read ? <MarkEmailUnreadIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title={t("notifications.deleteNotification")}>
                  <IconButton
                    size="small"
                    onClick={(event) => {
                      event.stopPropagation();
                      deleteUserNotification(notification.id);
                    }}
                    sx={{ mt: 0.25, color: "common.white" }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </MenuItem>
            ))}

            {notifications.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {t("notifications.empty")}
                </Typography>
              </MenuItem>
            )}

            <MenuItem
              onClick={() => {
                navigate("/notifications");
                handleCloseMenu(setNotificationsAnchor)();
              }}
            >
              <Typography
                color="primary"
                textAlign="center"
                sx={{ width: "100%" }}
              >
                {t("notifications.showAll")}
              </Typography>
            </MenuItem>
          </Menu>

          <Tooltip title={t("navbar.accountSettingsTooltip", { name: getDisplayName() })}>
            <IconButton
              onClick={handleOpenMenu(setSettingsAnchor)}
              color="inherit"
              sx={avatarButtonStyle}
            >
              <Avatar sx={{ width: 22, height: 22, fontSize: "0.75rem" }}>
                {getUserInitials()}
              </Avatar>
              <Box sx={{ display: { xs: "none", md: "block" } }}>
                <Typography fontWeight="bold">{getDisplayName()}</Typography>
              </Box>
            </IconButton>
          </Tooltip>

          <Menu
            anchorEl={settingsAnchor}
            open={Boolean(settingsAnchor)}
            onClose={handleCloseMenu(setSettingsAnchor)}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            PaperProps={{ sx: { bgcolor: "primary.main" } }}
          >
            <MenuItem>
              <Avatar
                sx={{ width: 22, height: 22, fontSize: "0.75rem", mr: 2 }}
              >
                {getUserInitials()}
              </Avatar>
              <Box>
                <Typography fontWeight="bold">{getDisplayName()}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || t("navbar.user")}
                </Typography>
              </Box>
            </MenuItem>
            <MenuItem disabled>
              <SettingsIcon sx={{ mr: 1 }} fontSize="small" />
              {t("navbar.accountSettings")}
            </MenuItem>
            <MenuItem disabled>
              <HelpIcon sx={{ mr: 1 }} fontSize="small" />
              {t("navbar.help")}
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                color: "common.white",
                "& .MuiSvgIcon-root": { color: "common.white" },
                "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.12)" },
              }}
            >
              <LogoutIcon sx={{ mr: 1 }} fontSize="small" />
              {t("navigation.logout")}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default NavBar;
